/**
 * SimulationRound
 *
 * Rounds save the state of the simulation at a given time.
 *
 * rounds are played in two parts:
 * all connections and events are handled first, then the actions can be modified by the player
 */

import Impulse from "@editor/items/impulse/Impulse";
import Effect from "@editor/items/effect/Effect";
import Action from "@editor/items/action/Action";

import type {
  PreventedAction,
  SimulationRoundItem,
  UnpayableAction
} from "./SimulationRoundItem";
import type { ImpulseItem } from "@editor/items/impulse/ImpulseItem";
import type { IncidentReason } from "../incident/IncidentReason";
import type { ResourceItem } from "../resource/ResourceItem";
import type { ActionActivation } from "./ActionActivation";
import type { BaseEffectItem } from "../effect/EffectItem";
import type { TargetItem } from "../target/TargetItem";
import type { IndexItem } from "../index/IndexItem";
import type { ExpressionParseContext } from "../effect/Expression";
import type { SetRequired } from "type-fest";
import type { ExpressionValue } from "../expression/expressionFunctions";
import { splitUid, type Uid } from "@/helpers/utility/Id";
import { cloneDeep } from "lodash-es";

export class SimulationRound implements SimulationRoundItem {
  id: number;
  uid: string;
  type: "simulationRound";
  createdAt: number;

  played: boolean;
  index: number;

  // values at the start of the round
  // (these are used for any condition evaluation)
  startElementValues: { [elementUid: string]: ExpressionValue };

  // each round keeps track of the values of the elements and resources
  elementValues: { [elementUid: string]: ExpressionValue; };

  activatedMomentums: number[];

  startRandomValues: { [key: string]: number | boolean; };
  randomValues: { [key: string]: number | boolean; };

  // save how often things were activated.
  // passed by round, so these values are simulation-wide.
  incidentCounts: { [incidentId: number]: number };
  actionCounts: { [actionId: number]: number };
  triggerCounts: { [triggerId: number]: number };

  pendingEffects: Effect[];
  impulses: {
    target: { [targetId: number]: Impulse };
    resource: { [resourceId: number]: Impulse };
  };

  /**
   * All incidents that were activated this round
   */
  activatedIncidents: IncidentReason[];
  activatedActions: ActionActivation[];

  /**
   * All planned actions for the round (succeeded and failed)
   */
  preventedActions: {
    [actionId: number]: PreventedAction;
  };
  notPayableActions: {
    [actionId: number]: UnpayableAction;
  };

  totalValue?: number; // calculated total value (to update at the end of a round)

  constructor(roundItem: Partial<SimulationRoundItem>) {
    this.id = roundItem.id ?? -1;
    this.uid = roundItem.uid ?? "";
    this.createdAt = roundItem.createdAt ?? Date.now();
    this.type = roundItem.type ?? "simulationRound";

    this.index = roundItem?.index ?? -1;

    this.played = roundItem.played ?? false;

    this.startElementValues = roundItem?.startElementValues || {};
    this.elementValues = roundItem?.elementValues || {};

    this.startRandomValues = roundItem?.startRandomValues || {};
    this.randomValues = roundItem?.randomValues || {};

    this.activatedMomentums = roundItem?.activatedMomentums || [];
    this.incidentCounts = roundItem?.incidentCounts || {};
    this.actionCounts = roundItem?.actionCounts || {};
    this.triggerCounts = roundItem?.triggerCounts || {};

    this.preventedActions = roundItem?.preventedActions || {};
    this.notPayableActions = roundItem?.notPayableActions || {};
    this.activatedIncidents = roundItem?.activatedIncidents || [];
    this.activatedActions = roundItem?.activatedActions || [];
    this.pendingEffects = (roundItem?.pendingEffects || []).map(
      (effect) => new Effect(effect)
    );

    this.impulses = { target: {}, resource: {} };
    const targetImpulses = roundItem.impulses?.target ?? {};
    for (const targetId in targetImpulses) {
      this.impulses.target[targetId] = new Impulse(
        targetImpulses[targetId]
      );
    }
    const resourceImpulses = roundItem.impulses?.["resource"] ?? {};
    for (const resourceId in resourceImpulses) {
      this.impulses["resource"][resourceId] = new Impulse(
        resourceImpulses[resourceId]
      );
    }

    // add a custom random number generator to this round
    // this.RNG = new seededRNG(gameRandomSeed + index);
  }

  /**
   * Add an impulse to the current round
   */
  addImpulse(
    impulseItem: ImpulseItem,
    affectedType: "target" | "resource",
    affectedId: number
  ) {
    if (impulseItem == null) {
      console.error("impulseItem is null", affectedType, affectedId);
      return false;
    }

    // make sure the list is set up correctly
    if (!this.impulses) {
      this.impulses = { target: {}, resource: {} };
    }
    // make sure the impulse is an impulse instance
    const impulse = new Impulse(impulseItem);

    // add the impulse to the list
    const typeImpulses = this.impulses[affectedType];
    if (typeImpulses == null) {
      console.error(`typeImpulses ${affectedType} is null`);
      return false;
    }
    typeImpulses[affectedId] = Impulse.add(typeImpulses[affectedId], impulse);
  }

  /**
   * Add effects to the round.
   * Add first effect impulse immediately, then add the rest of the effects to the pending list.
   */
  addEffects(
    effects: BaseEffectItem[],
    parseContext: SetRequired<ExpressionParseContext, "rounds" | "targets" | "resources" | "indices">
  ) {
    for (const effectItem of effects) {
      const effect = new Effect({ ...effectItem });

      if (effect.affectedId == -1) {
        continue;
      }

      // Ensure that the effect calculations use the round start values as base,
      // so that resource consumption changes don't affect effects.
      const rounds = parseContext.rounds;
      const round = rounds[this.index];
      const resettedRound = { ...round, elementValues: cloneDeep(round.startElementValues) }
      const resettedRounds = [...rounds];
      resettedRounds[this.index] = resettedRound;

      // add the first effect immediately:
      const impulse = effect.shiftImpulse({
        ...parseContext,
        rounds: resettedRounds,
        roundIndex: this.index
      });
      if (impulse) {
        this.addImpulse(impulse, effect.affectedType, effect.affectedId);
      }

      if (effect.isEmpty()) {
        continue;
      }

      // add any remaining effects to the pending list
      this.pendingEffects.push(effect);
    }
  }

  /**
   * Create impulses for all pending effects that are waiting to be activated.
   * Pending effects are passed from round to round.
   */
  addImpulsesFromPendingEffects(
    parseContext: SetRequired<ExpressionParseContext, "rounds" | "targets" | "resources" | "indices">
  ) {
    for (const effect of this.pendingEffects) {
      const impulse = effect.shiftImpulse({
        ...parseContext,
        roundIndex: this.index
      });

      if (effect.affectedId == null) {
        continue;
      }

      if (impulse) {
        // mark the impulse as delayed:
        for (const source of impulse.sourceValues) {
          source.type = "delayed_" + source.type;
        }
        this.addImpulse(impulse, effect.affectedType, effect.affectedId);
      }
    }

    this.pendingEffects = this.pendingEffects.filter(
      (effect) => !effect.isEmpty()
    );
  }

  /**
   * Add up all impulses of the current round.
   */
  applyImpulses(netItems: {
    target: { [targetId: number]: TargetItem };
    resource: { [resourceId: number]: ResourceItem };
    index: { [indexId: number]: IndexItem };
  }) {
    const roundImpulses = this.impulses || {};

    //  elements and resources
    for (const affectedType in roundImpulses) {
      if (affectedType !== "target" && affectedType !== "resource") {
        throw new Error(`invalid impulse type: ${affectedType}`);
      }
      const itemImpulses = roundImpulses[affectedType] || {};
      // go through ids
      for (const affectedId in itemImpulses) {
        const impulse = itemImpulses[affectedId];

        const affectedItem = netItems[affectedType][affectedId];

        if (affectedItem == null) {
          console.warn(`${affectedType} ${affectedId} not found`);
          continue;
        }

        this.applyImpulse(
          impulse,
          affectedType,
          parseInt(affectedId),
          affectedItem
        );
      }
    }
  }

  /**
   * Save the conditions that prevented a action
   */
  addPreventedAction(actionId: number, conditionIds: number[] | null) {
    this.preventedActions[actionId] = {
      actionId,
      conditionIds
    };
  }

  addNotPayableAction({
    actionId,
    elementUid,
    availableValue,
    needed
  }: {
    actionId: number;
    elementUid: Uid<"resource" | "target">;
    availableValue: number;
    needed: number;
  }) {
    this.notPayableActions[actionId] = {
      actionId,
      elementUid,
      availableValue,
      neededValue: needed
    };
  }

  addTriggeredIncident(triggeredIncident: IncidentReason) {
    const incidentId = triggeredIncident.incidentId;

    // save what triggered the incident
    this.activatedIncidents.push({
      incidentId: incidentId,
      conditionId: triggeredIncident.conditionId,
      triggerId: triggeredIncident.triggerId,
      rolledChance: triggeredIncident.rolledChance,
      neededChance: triggeredIncident.neededChance
    });

    // increment play counts
    this.incrementIncidentPlays(incidentId);
    if (triggeredIncident.triggerId != null) {
      this.incrementTriggerCounts(triggeredIncident.triggerId);
    }
  }

  /**
   * Remove resources from the round resources
   */
  applyCosts(costs: { [elementUid: Uid<"target" | "resource">]: number }) {
    for (const key in costs) {
      const uid = key as Uid<"target" | "resource">;
      const valueGroup = this.elementValues[uid];
      if (valueGroup == null) {
        console.warn(`value for ${uid} not found`);
        continue;
      }
      valueGroup.value = Number(valueGroup.value) + costs[uid];
    }
  }

  saveActionAsPlayed(actionId: number) {
    const currentPlays = this.actionCounts[actionId] || 0;
    this.actionCounts[actionId] = currentPlays + 1;
  }

  /**
   *
   */
  applyImpulse(
    impulse: {
      valueToSet?: number | null;
      pointsToAdd: number | null;
      valueBefore?: number;
      valueAfter?: number;
    },
    affectedType: "target" | "resource",
    affectedId: number,
    affectedItem: TargetItem | ResourceItem
  ) {
    // read the current value from the round values.
    let valueBefore = Number(this.elementValues[`${affectedType}_${affectedId}`]?.value) ?? 0;


    let valueAfter = valueBefore;
    if (impulse.valueToSet != null) {
      valueAfter = impulse.valueToSet;
    }
    if (impulse.pointsToAdd != null) {
      valueAfter += impulse.pointsToAdd;
    }

    // clamp values to max values
    valueAfter = Math.max(affectedItem.minValue ?? -Infinity, valueAfter);
    valueAfter = Math.min(valueAfter, affectedItem.maxValue ?? Infinity);

    // update the round values
    this.elementValues[`${affectedType}_${affectedId}`].value = valueAfter;

    // save the values to the impulse
    impulse.valueBefore = valueBefore;
    impulse.valueAfter = valueAfter;

    /*console.debug(
      `%capplied impulse to ${affectedType} ${affectedId} -> ${valueAfter}`,
      "background-color: limegreen; padding: 0.5em;"
    );*/
  }

  /**
   *
   */
  playAction(
    action: Action,
    rounds: SimulationRoundItem[],
    parseContext: SetRequired<ExpressionParseContext, "rounds" | "targets" | "resources" | "indices">,
    costs: { [elementUid: Uid<"target" | "resource">]: number }
  ) {
    // === CONDITIONS === //
    if (this.played) return false;

    // check if all costs are available
    const missingRessources = action.getMissingResources(
      this.elementValues,
      costs
    );
    console.debug("missingRessources", missingRessources);
    if (missingRessources.length > 0) {
      // save that the action was prevented in this round
      this.addNotPayableAction(missingRessources[0]);

      // end the action
      return false;
    }

    if (action.requirements.length > 0) {
      const fulfilledRequirements = action.getFulfilledRequirements(rounds);
      console.log("fulfilledRequirements", fulfilledRequirements);
      if (fulfilledRequirements.length == 0) {
        const unfulfilledConditions = action.getUnfulfilledConditions(rounds);
        this.addPreventedAction(
          action.id,
          unfulfilledConditions.map((c) => c.id)
        );
  
        return false;
      }
    }

    // can it still be played? (max amount not reached)
    if (action.hasBeenPlayedMaxTimes(this)) {
      this.addPreventedAction(action.id, null);
      return false;
    }

    // conditions ok, costs ok. play it.

    // deduct the costs of the action
    this.applyCosts(costs);

    // save the cost in the resource impulse:
    for (const key in costs) {
      const elementUid = key as Uid<"target" | "resource">;
      const { type, id } = splitUid(elementUid);
      // create the impulse if it doesn't exist.
      if (this.impulses[type][id] == null) {
        this.impulses[type][id] = new Impulse({
          pointsToAdd: 0,
          sourceValues: []
        });
      }
      // add the impulse in the sourcevalues
      // (don't increment the core value of the impulse)
      this.impulses[type][id].sourceValues.push({
        type: "action",
        id: action.id,
        value: costs[elementUid],
        effectType: "cost"
      });
    }

    // save that the action was updated
    this.activatedActions.push({
      actionId: action.id,
      costs: costs
    });

    // update the action play count of the round:
    this.saveActionAsPlayed(action.id);

    // add all the effects of the action:
    this.addEffects(action.effects, {
      ...parseContext,
      rounds,
    });

    return true;
  }

  incrementIncidentPlays(incidentId: number) {
    const currentValue = this.incidentCounts[incidentId] || 0;
    this.incidentCounts[incidentId] = currentValue + 1;
  }

  incrementTriggerCounts(triggerId: number) {
    const currentValue = this.triggerCounts[triggerId] || 0;
    this.triggerCounts[triggerId] = currentValue + 1;
  }

  static create(newRoundData: SimulationRoundItem): SimulationRoundItem {
    return {
      id: newRoundData.id,
      uid: newRoundData.uid,
      type: "simulationRound",
      index: newRoundData.index,
      played: newRoundData.played ?? false,
      impulses: newRoundData.impulses ?? { target: {}, resource: {} },
      pendingEffects: newRoundData.pendingEffects ?? [],

      startElementValues: newRoundData.startElementValues ?? {},
      elementValues: newRoundData.elementValues ?? {},

      startRandomValues: newRoundData.startRandomValues ?? {},
      randomValues: newRoundData.randomValues ?? {},

      activatedMomentums: [...(newRoundData.activatedMomentums ?? [])],
      incidentCounts: newRoundData.incidentCounts ?? {},
      actionCounts: newRoundData.actionCounts ?? {},
      triggerCounts: newRoundData.triggerCounts ?? {},
      preventedActions: newRoundData.preventedActions ?? {},
      notPayableActions: newRoundData.notPayableActions ?? {},
      activatedIncidents: newRoundData.activatedIncidents ?? [],
      activatedActions: newRoundData.activatedActions ?? [],
      totalValue: newRoundData.totalValue ?? undefined,

      createdAt: Date.now()
    };
  }
}

export default SimulationRound;
